import { useTranslation } from "react-i18next"

import { EyeIcon, GridIcon } from "src/components/icons"

import "./MapControls.css"

/* The grid eye and the fog switch, on the map they change (owner's ask, 2026-09-04).

   They used to live inside the minimap's panel, which was defensible while the minimap held the
   bottom of the dock and indefensible once it floated over the board: a control that changes the
   *map* was sitting inside a picture *of* the map, in the opposite corner from where the map is
   worked. The lobby had a third arrangement again — its own pair, top-right — so the same two
   buttons were in two places depending on which screen you were on. One component now, one corner,
   both screens.

   Bottom-left, and the action bar is moved right rather than up: the controls are a fixed landmark
   and the bar comes and goes with a selection, so the thing that is always there gets the corner.

   **There is no +/- here, or anywhere** (owner, 2026-09-14, then 2026-09-20): the mouse wheel
   zooms, about the pointer, and the drag travels, so the pair was a second way to do what the
   hand already does. The phone kept a pair of its own beside the pinch until 2026-09-20, when it
   went for the same reason — the pinch is the phone's wheel.

   `onToggleGrid` is optional: basic training passes none, so the eye is not drawn at all. The
   lesson holds the grid and its coordinates on — the coach names cells by them — and a switch a
   learner may not press is better absent than greyed.

   The lobby is no longer a caller: with the zoom gone it had nothing left to show here.

   `fogPreview` is optional too — `{ on, onToggle }`, passed only by a game on a server that runs
   without fog (testing). It is the tester's switch between the whole map and the fogged view a
   player would get (owner's S19, 2026-09-05: it was a banner in the radar's corner). Amber while
   previewing, the same state colour the settings switch wears, because a tester who cannot tell
   "fogged by choice" from "fog broken" misreads every empty cell.

   `speed` is optional — `{ value, onChange }`: how fast a round's show plays, x1 → x2 → x4 → x1 on
   each press (owner, 2026-10-26, requirement 36 M10), on top of the grid's button. `top` is what
   stands above it — REACT off the phone (owner, the same day: "put the :D on top of the x1, so the
   3 buttons are aligned"). */

const MapControls = ({ gridVisible = true, onToggleGrid, fogPreview = null, speed = null, top = null, className = "" }) => {
  const { t } = useTranslation()
  return (
    <div className={`map-controls${className ? ` ${className}` : ""}`}>
      {top}
      {speed && (
        <button
          type="button"
          className={`speed-toggle${speed.value > 1 ? " fast" : ""}`}
          onClick={speed.onChange}
          title={t("game.miniMap.showSpeed", { speed: speed.value })}
          aria-label={t("game.miniMap.showSpeed", { speed: speed.value })}
        >
          x{speed.value}
        </button>
      )}
      {onToggleGrid && (
        <button
          type="button"
          className={`grid-toggle${gridVisible ? "" : " off"}`}
          onClick={onToggleGrid}
          aria-pressed={!gridVisible}
          title={gridVisible ? t("game.miniMap.hideGrid") : t("game.miniMap.showGrid")}
          aria-label={gridVisible ? t("game.miniMap.hideGrid") : t("game.miniMap.showGrid")}
        >
          <GridIcon size={22} off={!gridVisible} />
        </button>
      )}
      {fogPreview && (
        <button
          type="button"
          className={`fog-toggle${fogPreview.on ? " on" : ""}`}
          onClick={fogPreview.onToggle}
          role="switch"
          aria-checked={Boolean(fogPreview.on)}
          aria-label={t("settings.fogPreview")}
          title={`${t("settings.fogPreview")} · ${fogPreview.on ? t("settings.fogPreviewOn") : t("settings.fogPreviewOff")}`}
        >
          {/* Closed while previewing the fog a player gets, open on the whole map. */}
          <EyeIcon size={20} closed={Boolean(fogPreview.on)} />
        </button>
      )}
    </div>
  )
}

export default MapControls
